import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function n(v) {
  const s = String(v ?? "").replace(/,/g, "").trim();
  const num = parseFloat(s);
  return isNaN(num) ? 0 : num;
}

const EMIPaymentReportAllPrint = () => {
  const q = useQuery();

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ take params from URL (same as your listing page searchData)
  const searchParams = useMemo(
    () => ({
      enquiryNumber: q.get("enquiryNumber") || "",
      customerName: q.get("customerName") || "",
      customerId: q.get("customerId") || "",
      assetSubType: q.get("assetSubType") || "", // assetsubtypeid
      doB: q.get("doB") || "", // YYYY-MM-DD
      paymentStatus: q.get("paymentStatus") || "",
    }),
    [q]
  );

  // ✅ Call SEARCH API
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await axios.get(`${BASE_URL}/api/emiListing/search`, {
          params: searchParams,
        });
        console.log("response:", res.data.data);

        if (!res.data?.success) {
          throw new Error(res.data?.message || "Failed to load report");
        }

        // backend returns { data: [...] }
        const rows = res.data?.data || [];

        // map backend keys -> print table keys
        const mapped = rows.map((r, idx) => ({
          sNo: r.slNo ?? idx + 1,
          customerId: r.customerId ?? "",
          customerName: r.customerName ?? "",
          productName: r.assetTypeModel ?? r.productName ?? "",
          plotNoAssetCode: r.assetCode ?? r.plotNumber ?? "",
          assetValue: r.purchaseValue[0] ?? "",
          interestRate: r.interestRate ?? "0.00",
          overdueInterestRate: r.overdueInterest ?? "0.00",
          tenurePeriod: r.tenurePeriod ?? "",
          emiAmountMonth: r.emiAmountMonth ?? "",
          emiActiveDate: r.emiActivateDate ?? "",
          dueRemaining: r.dueRemaining ?? "",
          currentMonthPaymentStatus: r.currentMonthPaymentStatus ?? r.paymentStatus ?? "",
        }));

        setReportData(mapped);

        // optional auto-print
        setTimeout(() => window.print(), 500);
      } catch (e) {
        setError(e?.message || "Error loading report");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [searchParams]);

  // ✅ total EMI amount
  const totalEMIAmount = reportData.reduce((sum, item) => sum + n(item.emiAmountMonth), 0);

  return (
    <div className="max-w-full mx-auto p-4 bg-white font-sans text-xs print:p-0 print:max-w-none">
      {/* Header Section */}
      <div className="border-2 border-black mb-0">
        <div className="border-b border-black flex">
          <div className="w-32 border-r border-black p-2 flex items-center justify-center">
            <div className="bg-black text-white px-2 py-1 text-xs font-bold text-center">
              <div>NISSI</div>
              <div>INFOTECH PVT LTD</div>
            </div>
          </div>

          <div className="flex-1 p-3">
            <div className="text-center">
              <h1 className="text-lg font-bold mb-1">Nissi Infotech</h1>
              <div className="text-xs">
                <div>Nissi</div>
                <div className="font-bold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony, kavundampalayam,641600, Tamil Nadu, India.</div>
                <div>Phone No: 9003744644 infraconsoft-implementation@nissiinfotech.com</div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center py-2 border-b border-black font-bold">
          EMI Payment Report
        </div>
      </div>

      {/* Loading/Error */}
      {loading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          <div className="mt-2">Loading report data...</div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <div className="font-bold">Error loading report:</div>
          <div>{error}</div>
        </div>
      )}

      {/* Table Section */}
      <div className="border-l-2 border-r-2 border-b-2 border-black overflow-x-auto">
        <table className="w-full text-xs min-w-max">
          <thead>
            <tr className="border-b border-black bg-gray-100">
              <th className="border-r border-black px-1 py-2 w-12 text-center">S.No</th>
              <th className="border-r border-black px-1 py-2 w-20 text-center">Customer ID</th>
              <th className="border-r border-black px-1 py-2 w-24 text-center">Customer Name</th>
              <th className="border-r border-black px-1 py-2 w-28 text-center">Product Name</th>
              <th className="border-r border-black px-1 py-2 w-24 text-center">Plot No / Asset Code</th>
              <th className="border-r border-black px-1 py-2 w-24 text-center">Asset Value</th>
              <th className="border-r border-black px-1 py-2 w-20 text-center">Interest rate</th>
              <th className="border-r border-black px-1 py-2 w-20 text-center">Overdue Interest rate</th>
              <th className="border-r border-black px-1 py-2 w-20 text-center">Tenure Period</th>
              <th className="border-r border-black px-1 py-2 w-24 text-center">EMI Amount/Month</th>
              <th className="border-r border-black px-1 py-2 w-24 text-center">EMI Active Date</th>
              <th className="border-r border-black px-1 py-2 w-20 text-center">Due Remaining</th>
              <th className="px-1 py-2 w-28 text-center">Current Month Payment Status</th>
            </tr>
          </thead>

          <tbody>
            {reportData.map((item, index) => (
              <tr key={index} className="border-b border-gray-300">
                <td className="border-r border-black px-1 py-1 text-center">{item.sNo}</td>
                <td className="border-r border-black px-1 py-1 text-center">{item.customerId}</td>
                <td className="border-r border-black px-1 py-1">{item.customerName}</td>
                <td className="border-r border-black px-1 py-1">{item.productName}</td>
                <td className="border-r border-black px-1 py-1 text-center">{item.plotNoAssetCode}</td>
                <td className="border-r border-black px-1 py-1 text-right">{item.assetValue}</td>
                <td className="border-r border-black px-1 py-1 text-center">{item.interestRate}</td>
                <td className="border-r border-black px-1 py-1 text-center">{item.overdueInterestRate}</td>
                <td className="border-r border-black px-1 py-1 text-center">{item.tenurePeriod}</td>
                <td className="border-r border-black px-1 py-1 text-right">{item.emiAmountMonth}</td>
                <td className="border-r border-black px-1 py-1 text-center">{item.emiActiveDate}</td>
                <td className="border-r border-black px-1 py-1 text-center">{item.dueRemaining}</td>
                <td className="px-1 py-1">{item.currentMonthPaymentStatus}</td>
              </tr>
            ))}

            {/* Total Row */}
            <tr className="border-t-2 border-black font-bold bg-gray-50">
              <td colSpan="9" className="border-r border-black px-1 py-2 text-right">
                {totalEMIAmount.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
              <td colSpan="4" className="px-1 py-2"></td>
            </tr>

            {(!loading && reportData.length === 0) && (
              <tr>
                <td colSpan="13" className="py-6 text-center">No records found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          table {
            border-collapse: collapse !important;
          }
          th,
          td {
            border: 1px solid black !important;
          }
          .bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
          .bg-gray-50 {
            background-color: #f9fafb !important;
          }
          .bg-black {
            background-color: black !important;
            color: white !important;
          }
          @page {
            margin: 0.5in;
            size: A4 landscape;
          }
        }
      `}</style>
    </div>
  );
};

export default EMIPaymentReportAllPrint;
